import type { SensitivityModelCurve } from './sensitivityModelCalculations';

export interface MonteCarloSettings {
  simulations: number;
  displayEndT: number;
  productivityPct: number;
  declinePct: number;
  engineeringPct: number;
  seed: number;
}

export interface MonteCarloSummary {
  mean: number;
  std: number;
  cvPct: number;
  spreadPct: number;
  downsidePct: number;
  upsidePct: number;
  uncertaintyLevel: '较低' | '中等' | '较高';
}

export interface PercentileCurvePoint {
  t: number;
  p90: number;
  p50: number;
  p10: number;
}

export interface MonteCarloResult {
  simulations: number;
  cumulativeSamples: number[];
  p90Cumulative: number;
  p50Cumulative: number;
  p10Cumulative: number;
  p90FinalRate: number;
  p50FinalRate: number;
  p10FinalRate: number;
  curve: PercentileCurvePoint[];
}

export function runMonteCarlo(curve: SensitivityModelCurve, settings: MonteCarloSettings): MonteCarloResult | null {
  if (curve.points.length < 2 || settings.displayEndT <= curve.points[0].t) return null;

  const count = Math.max(100, Math.min(10000, Math.round(settings.simulations)));
  const startT = curve.points[0].t;
  const endT = settings.displayEndT;
  const pointCount = 121;
  const times = Array.from({ length: pointCount }, (_, i) => startT + (endT - startT) * i / (pointCount - 1));
  const qSamples: number[][] = Array.from({ length: pointCount }, () => []);
  const cumulativeSamples: number[] = [];
  const finalRateSamples: number[] = [];
  const rng = createSeededRandom(settings.seed);

  const productivityRange = Math.max(0, settings.productivityPct) / 100;
  const declineRange = Math.max(0, settings.declinePct) / 100;
  const engineeringRange = Math.max(0, settings.engineeringPct) / 100;

  for (let simulation = 0; simulation < count; simulation += 1) {
    const productivityFactor = triangular(rng, Math.max(0.01, 1 - productivityRange), 1, 1 + productivityRange);
    const declineSpeedFactor = triangular(rng, Math.max(0.05, 1 - declineRange), 1, 1 + declineRange);
    const engineeringFactor = triangular(rng, Math.max(0.01, 1 - engineeringRange), 1, 1 + engineeringRange);
    const productionFactor = productivityFactor * engineeringFactor;

    const rates = times.map((t) => {
      const transformedT = startT + (t - startT) * declineSpeedFactor;
      return Math.max(0, interpolateCurve(curve, transformedT) * productionFactor);
    });

    let cumulative = 0;
    for (let i = 1; i < times.length; i += 1) cumulative += 0.5 * (rates[i - 1] + rates[i]) * (times[i] - times[i - 1]);

    cumulativeSamples.push(cumulative);
    finalRateSamples.push(rates[rates.length - 1]);
    rates.forEach((q, index) => qSamples[index].push(q));
  }

  const curvePercentiles = times.map((t, index) => ({
    t,
    p90: percentile(qSamples[index], 0.10),
    p50: percentile(qSamples[index], 0.50),
    p10: percentile(qSamples[index], 0.90),
  }));

  return {
    simulations: count,
    cumulativeSamples,
    p90Cumulative: percentile(cumulativeSamples, 0.10),
    p50Cumulative: percentile(cumulativeSamples, 0.50),
    p10Cumulative: percentile(cumulativeSamples, 0.90),
    p90FinalRate: percentile(finalRateSamples, 0.10),
    p50FinalRate: percentile(finalRateSamples, 0.50),
    p10FinalRate: percentile(finalRateSamples, 0.90),
    curve: curvePercentiles,
  };
}

function interpolateCurve(curve: SensitivityModelCurve, t: number) {
  const points = curve.points;
  if (t <= points[0].t) return points[0].q;
  if (t >= points[points.length - 1].t) return points[points.length - 1].q;

  let low = 0;
  let high = points.length - 1;

  while (high - low > 1) {
    const mid = Math.floor((low + high) / 2);
    if (points[mid].t <= t) low = mid;
    else high = mid;
  }

  const left = points[low];
  const right = points[high];
  const fraction = (t - left.t) / Math.max(1e-12, right.t - left.t);
  return left.q + fraction * (right.q - left.q);
}

function percentile(values: number[], probability: number) {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const position = Math.max(0, Math.min(1, probability)) * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (position - lower) * (sorted[upper] - sorted[lower]);
}

function triangular(random: () => number, min: number, mode: number, max: number) {
  if (max <= min) return min;
  const u = random();
  const f = (mode - min) / (max - min);
  return u < f ? min + Math.sqrt(u * (max - min) * (mode - min)) : max - Math.sqrt((1 - u) * (max - min) * (max - mode));
}

function createSeededRandom(seed: number) {
  let state = Math.abs(Math.floor(seed)) % 2147483647;
  if (state === 0) state = 1;

  return () => {
    state = state * 16807 % 2147483647;
    return (state - 1) / 2147483646;
  };
}

export function summarizeMonteCarlo(result: MonteCarloResult): MonteCarloSummary {
  const values = result.cumulativeSamples;
  const mean = values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);
  const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / Math.max(1, values.length);
  const std = Math.sqrt(variance);
  const cvPct = mean > 0 ? std / mean * 100 : 0;
  const spreadPct = result.p50Cumulative > 0 ? (result.p10Cumulative - result.p90Cumulative) / result.p50Cumulative * 100 : 0;
  const downsidePct = result.p50Cumulative > 0 ? (result.p50Cumulative - result.p90Cumulative) / result.p50Cumulative * 100 : 0;
  const upsidePct = result.p50Cumulative > 0 ? (result.p10Cumulative - result.p50Cumulative) / result.p50Cumulative * 100 : 0;

  let uncertaintyLevel: MonteCarloSummary['uncertaintyLevel'] = '较低';
  if (spreadPct > 40) uncertaintyLevel = '较高';
  else if (spreadPct > 20) uncertaintyLevel = '中等';

  return { mean, std, cvPct, spreadPct, downsidePct, upsidePct, uncertaintyLevel };
}